import Link from 'next/link';
import { FC, useEffect, useState } from 'react';
import { FaPause, FaPlay } from 'react-icons/fa';
import { useInView } from 'react-intersection-observer';

import { appSettings } from '@/settings/app-settings';
import { IMG_PLACEHOLDER } from '@/ux-legacy/uikit/utils/img-placeholder';

import Content from '../Content/Content';
import { Highlight } from '../Highlights';

type PlayContentProps = {
  item: Highlight;
  isActive: boolean;
  playNext: () => void;
};

const PlayContent: FC<PlayContentProps> = ({ item, isActive, playNext }) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [screen, setScreen] = useState<string>('');

  const [show, setShow] = useState<boolean>(false);

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const updateCurrentIndex = () => {
    // setCurrentIndex((prevIndex) => {
    //   return prevIndex < 1 - 1 ? prevIndex + 1 : 0;
    // });
    playNext();
  };

  useEffect(() => {
    const handleChange = () => {
      setScreen(window.innerWidth > 760 ? 'desktop' : 'mobile');
    };
    handleChange();
    window.addEventListener('resize', handleChange);

    return () => {
      window.removeEventListener('resize', handleChange);
    };
  }, []);

  useEffect(() => {
    if (inView) {
      setShow(true);
    }
  }, [inView]);

  return (
    <div ref={ref} className="w-full h-full flex bg-transparent relative">
      {isActive && (
        <div className="absolute top-0 left-0 py-2 -translate-y-[100%] z-[99] w-full flex flex-col gap-y-1">
          <PlayProgessIndicator
            count={1}
            currentIndex={currentIndex}
            isActive={isActive}
            isPaused={isPaused}
            setCurrentIndex={setCurrentIndex}
            updateCurrentIndex={updateCurrentIndex}
          />
          <div className="w-full flex justify-between items-center gap-x-2">
            <Link href={`/p/${item.userInfo.Handle}`} className="w-fit flex items-center gap-x-2 px-2">
              <img
                src={
                  item.userInfo.DisplayPic
                    ? appSettings.api.S3_MEDIA_COMPRESSED + item.userInfo.DisplayPic
                    : IMG_PLACEHOLDER
                }
                alt="profile"
                className="w-[36px] h-[36px] object-cover rounded-full border border-opacity-30 border-black shadow-sm "
              ></img>
              <div className="font-bold text-white truncate">
                {item.userInfo.Name || item.userInfo.Handle}
              </div>
            </Link>

            {isActive && (
              <button
                className="h-7 w-7 flex p-2 text-white"
                onClick={() => {
                  setIsPaused(!isPaused);
                }}
              >
                {isPaused ? <FaPlay className="w-full h-full" /> : <FaPause className="w-full h-full" />}
              </button>
            )}
          </div>
        </div>
      )}

      {show && (
        <div
          className="w-full h-full flex"
          style={{
            pointerEvents: screen == 'mobile' && item.content.contentType != 'asset' ? 'none' : 'auto',
          }}
        >
          <Content content={item.content.content} contentType={item.content.contentType} />
        </div>
      )}

      <div
        className={`absolute top-0 left-0 bg-black/50 w-full h-full flex items-center justify-center transition-opacity duration-300 pointer-events-none ${
          isActive ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <div className="w-fit h-fit flex flex-col items-center gap-y-1">
          <img
            src={
              item.userInfo.DisplayPic
                ? appSettings.api.S3_MEDIA_COMPRESSED + item.userInfo.DisplayPic
                : IMG_PLACEHOLDER
            }
            className="w-28 h-28 object-cover rounded-full"
            alt=""
          ></img>
          <span className="text-xl text-white font-bold">{item.userInfo.Name}</span>
        </div>
      </div>
    </div>
  );
};

const PlayProgessIndicator: FC<{
  currentIndex: number;
  count: number;
  isActive: boolean;
  isPaused: boolean;
  setCurrentIndex: (index: number) => void;
  updateCurrentIndex: () => void;
}> = ({ currentIndex, isActive, isPaused, count, setCurrentIndex, updateCurrentIndex }) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      setWidth((prevWidth) => {
        const deltaTime = 8;
        const deltaWidth = 100 * (deltaTime / 6000);
        return Math.min(prevWidth + deltaWidth);
      });
    };

    let widthUpdateInterval: NodeJS.Timeout;

    if (isActive && !isPaused) {
      widthUpdateInterval = setInterval(updateWidth, 8);
    }

    return () => {
      clearInterval(widthUpdateInterval);
    };
  }, [isActive, isPaused, currentIndex]);

  useEffect(() => {
    if (width >= 100) {
      updateCurrentIndex();
      setWidth(0);
    }
  }, [width]);

  return (
    <div className="w-full flex gap-x-2">
      {Array.from({ length: count }).map((each, index) => {
        return (
          <button
            key={index}
            className={`h-0.5  ${
              index < currentIndex ? 'bg-white/70 border border-black border-opacity-10' : 'bg-black/50'
            } w-full shadow-xl flex rounded-md`}
            onClick={() => setCurrentIndex(index)}
          >
            {currentIndex == index && (
              <div className={`h-full bg-white/80  rounded-md`} style={{ width: `${width}%` }}></div>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default PlayContent;
